import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as telemarketingService from '../../services/crm/telemarketingService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import ENV from '../../config/env';

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

export default function CallDetail() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // @ts-ignore - organization_id exists on user object
  const activeOrgId = user?.organization_id || user?.user_metadata?.organization_id;
  const [call, setCall] = useState<telemarketingService.TelemarketingCall | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (callId) {
      loadCall();
    }
  }, [callId]);

  // Standalone page - header managed inline

  const loadCall = async () => {
    try {
      setLoading(true);
      const callData = await telemarketingService.getCall(callId!);
      setCall(callData);
    } catch (error: any) {
      console.error('Failed to load call:', error);
      toast.error('Failed to load call details');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading || !call) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const recordingSrc = call.recording_url && user?.id
    ? `${ENV.API_URL}/api/telemarketing/calls/${call.id}/recording?userId=${encodeURIComponent(user.id)}`
    : undefined;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Call Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{call.phone_number}</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Duration</div>
            <div className="text-2xl font-bold text-gray-900">
              {call.duration ? formatDuration(call.duration) : 'N/A'}
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Sentiment</div>
            <div className="text-2xl font-bold text-purple-600">
              {call.sentiment_score?.toFixed(2) || 'N/A'}
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Lead Score</div>
            <div className="text-2xl font-bold text-purple-600">
              {call.lead_score || 0}<span className="text-sm">/100</span>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Outcome</div>
            <div className="text-sm font-semibold capitalize">
              {call.outcome ? call.outcome.replace('_', ' ') : 'In Progress'}
            </div>
          </div>
        </div>

        {recordingSrc && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <audio controls className="w-full" src={recordingSrc}>
              Your browser does not support audio.
            </audio>
          </div>
        )}
      </div>

      {/* Transcript */}
      {call.transcript && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Call Transcript</h3>
          <div className="prose max-w-none">
            <pre className="whitespace-pre-wrap text-sm text-gray-700">{call.transcript}</pre>
          </div>
        </div>
      )}

      {/* AI Insights */}
      {call.next_action && (
        <div className="bg-purple-50 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Recommended Next Action</h3>
          <p className="text-gray-700">{call.next_action}</p>
        </div>
      )}
    </div>
  );
}
